import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../services/api';

interface Profile {
  id: string;
  name: string;
  email: string;
  phone: string;
  profile_picture: string | null;
  gym_name: string | null;
  gym_description: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  latitude: number | null;
  longitude: number | null;
  operating_hours: Record<string, { open: string; close: string }>;
  amenities: string[];
  capacity: number;
  tier: string;
  is_verified: boolean;
  created_at: string;
}

interface BankAccount {
  id: string;
  account_holder_name: string;
  account_number: string;
  ifsc_code: string;
  bank_name: string;
  branch_name: string | null;
  account_type: string;
  is_verified: boolean;
  is_primary: boolean;
  verification_status: string;
  created_at: string;
}

interface NotificationPreferences {
  email_notifications: boolean;
  sms_notifications: boolean;
  push_notifications: boolean;
  notification_types: {
    new_booking: boolean;
    booking_cancelled: boolean;
    payout_processed: boolean;
    system_updates: boolean;
    low_balance: boolean;
    new_review: boolean;
  };
  quiet_hours: {
    enabled: boolean;
    start_time: string;
    end_time: string;
  };
}

interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  key: string | null;
  last_used_at: string | null;
  usage_count: number;
  is_active: boolean;
  created_at: string;
  revoked_at: string | null;
}

interface SettingsState {
  profile: Profile | null;
  bankAccounts: BankAccount[];
  notificationPreferences: NotificationPreferences | null;
  apiKeys: ApiKey[];
  isLoading: boolean;
  error: string | null;
}

const initialState: SettingsState = {
  profile: null,
  bankAccounts: [],
  notificationPreferences: null,
  apiKeys: [],
  isLoading: false,
  error: null
};

export const fetchProfile = createAsyncThunk(
  'settings/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/settings/profile');
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch profile');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'settings/updateProfile',
  async (profileData: any, { rejectWithValue }) => {
    try {
      const response = await apiClient.put('/settings/profile', profileData);
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update profile');
    }
  }
);

export const changePassword = createAsyncThunk(
  'settings/changePassword',
  async ({ current_password, new_password }: { current_password: string; new_password: string }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/settings/change-password', {
        current_password,
        new_password
      });
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to change password');
    }
  }
);

export const fetchBankAccounts = createAsyncThunk(
  'settings/fetchBankAccounts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/settings/bank-accounts');
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch bank accounts');
    }
  }
);

export const addBankAccount = createAsyncThunk(
  'settings/addBankAccount',
  async (accountData: any, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/settings/bank-accounts', accountData);
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add bank account');
    }
  }
);

export const updateBankAccount = createAsyncThunk(
  'settings/updateBankAccount',
  async ({ accountId, updates }: { accountId: string; updates: any }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`/settings/bank-accounts/${accountId}`, updates);
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update bank account');
    }
  }
);

export const setPrimaryBankAccount = createAsyncThunk(
  'settings/setPrimaryBankAccount',
  async (accountId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/settings/bank-accounts/${accountId}/set-primary`);
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to set primary account');
    }
  }
);

export const fetchNotificationPreferences = createAsyncThunk(
  'settings/fetchNotificationPreferences',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/settings/notification-preferences');
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch notification preferences');
    }
  }
);

export const updateNotificationPreferences = createAsyncThunk(
  'settings/updateNotificationPreferences',
  async (preferences: any, { rejectWithValue }) => {
    try {
      const response = await apiClient.put('/settings/notification-preferences', preferences);
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update notification preferences');
    }
  }
);

export const fetchApiKeys = createAsyncThunk(
  'settings/fetchApiKeys',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/settings/api-keys');
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch API keys');
    }
  }
);

export const generateApiKey = createAsyncThunk(
  'settings/generateApiKey',
  async (name: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/settings/api-keys', { name });
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to generate API key');
    }
  }
);

export const revokeApiKey = createAsyncThunk(
  'settings/revokeApiKey',
  async (keyId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(`/settings/api-keys/${keyId}`);
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to revoke API key');
    }
  }
);

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profile = action.payload;
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(changePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchBankAccounts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBankAccounts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bankAccounts = action.payload;
      })
      .addCase(fetchBankAccounts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(addBankAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addBankAccount.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(addBankAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateBankAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateBankAccount.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateBankAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(setPrimaryBankAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(setPrimaryBankAccount.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(setPrimaryBankAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchNotificationPreferences.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNotificationPreferences.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notificationPreferences = action.payload;
      })
      .addCase(fetchNotificationPreferences.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateNotificationPreferences.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateNotificationPreferences.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateNotificationPreferences.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchApiKeys.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchApiKeys.fulfilled, (state, action) => {
        state.isLoading = false;
        state.apiKeys = action.payload;
      })
      .addCase(fetchApiKeys.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(generateApiKey.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(generateApiKey.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(generateApiKey.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(revokeApiKey.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(revokeApiKey.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(revokeApiKey.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  }
});

export const { clearError } = settingsSlice.actions;
export default settingsSlice.reducer;
